import type * as Item from "../../lib/models/item.server.ts"
import type * as LeasedTag from "../../lib/models/leased-tag.server.ts"

import * as UI from "@iyk/ui"
import * as React from "react"

import { getSingularOrPluralTerm } from "@iyk/localization"
import { Await, Link, useNavigate } from "@remix-run/react"
import { useIykUser } from "../../lib/hooks/use-iyk-user.ts"
import { useLang } from "../../lib/lang/use-lang.ts"
import { CommonItemsDialog } from "./common-items-dialog.tsx"

//#region OwnerHoverCard

export const OwnerHoverCard = ({ owner, ownerStats }: OwnerHoverCardProps) => {
  const lang = useLang()
  const { iykUser } = useIykUser()
  const { mintedItemsCount, itemsInCommon = [] } = ownerStats

  const ownerItemsText = getOwnerItemsPluralizedText(mintedItemsCount, "item", "items", lang.name)

  const itemsInCommonQuantity = itemsInCommon.length
  const hasItemsInCommon = itemsInCommonQuantity > 0
  const isDifferentUser = iykUser && iykUser.id !== owner.id

  const showCommonItemsDialog = Boolean(hasItemsInCommon && isDifferentUser && itemsInCommon)

  return (
    <UI.HoverCard openDelay={250} closeDelay={250}>
      <UI.HoverCardTrigger className="w-fit">
        <OwnerHoverCardTrigger owner={owner} />
      </UI.HoverCardTrigger>
      <UI.HoverCardContent
        side="bottom"
        align="start"
        sideOffset={6}
        className="border border-gray-4 [box-shadow:0px_3px_12px_0px_rgba(0,0,0,0.09)] w-full min-w-[350px] rounded-none"
      >
        <OwnerHoverCardContent
          owner={owner}
          ownerStats={ownerStats}
          ownerItemsText={ownerItemsText}
          showCommonItemsDialog={showCommonItemsDialog}
        />
      </UI.HoverCardContent>
    </UI.HoverCard>
  )
}

//#endregion

//#region AsyncOwnerHoverCard

export const AsyncOwnerHoverCard = ({ owner, ownerStatsPromise }: AsyncOwnerHoverCardProps) => {
  return (
    <React.Suspense fallback={<OwnerHoverCardTrigger owner={owner} />}>
      <Await resolve={ownerStatsPromise}>
        {(ownerStats) => <OwnerHoverCard owner={owner} ownerStats={ownerStats} />}
      </Await>
    </React.Suspense>
  )
}

//#endregion

//#region OwnerHoverCardTrigger

const OwnerHoverCardTrigger = ({ owner }: { owner: OwnerHoverCardProps["owner"] }) => {
  const navigate = useNavigate()

  return (
    <div
      onClick={() => navigate(`/profile/${owner.username}`)}
      className="flex items-center gap-3 cursor-pointer select-none"
    >
      <UI.Avatar className="size-7" src={owner.avatarUrl || undefined} />
      <div className="flex flex-col gap-0.5">
        <span className="text-xs-upper">Owner</span>
        <span className="text-sm">{owner.username}</span>
      </div>
    </div>
  )
}

//#endregion

//#region OwnerHoverCardContent

const OwnerHoverCardContent = ({
  owner,
  ownerStats,
  ownerItemsText,
  showCommonItemsDialog,
}: OwnerHoverCardContentProps) => {
  const { iykUser } = useIykUser()

  return (
    <div className="flex flex-col items-start gap-4 p-4">
      <Link to={`/profile/${owner.username}`} className="flex items-center gap-4">
        <UI.Avatar className="size-8" src={owner?.avatarUrl || undefined} />
        <div className="flex flex-col gap-0.5">
          <span className="text-sm">{owner.username}</span>
          <span className="text-gray-11 text-xs">{ownerItemsText}</span>
        </div>
      </Link>
      {showCommonItemsDialog && iykUser && (
        <CommonItemsDialog
          currentUser={iykUser}
          targetUser={{
            id: owner.id,
            username: owner.username,
            avatarUrl: owner.avatarUrl,
          }}
          items={ownerStats.itemsInCommon}
          usedInFloatingElement
        />
      )}
    </div>
  )
}

//#endregion

//#region Utils

const getOwnerItemsPluralizedText = (
  count: number,
  singular: string,
  plural: string,
  language: string,
) => `${count} ${getSingularOrPluralTerm(count, singular, plural, language)}`

//#endregion

//#region Types

type OwnerHoverCardProps = {
  owner: LeasedTag.Owner
  ownerStats: {
    mintedItemsCount: number
    itemsInCommon: Item.ProfilePreview[]
  }
}

type OwnerHoverCardContentProps = {
  owner: OwnerHoverCardProps["owner"]
  ownerStats: OwnerHoverCardProps["ownerStats"]
  ownerItemsText: string
  showCommonItemsDialog: boolean
}

type AsyncOwnerHoverCardProps = {
  owner: LeasedTag.Owner
  ownerStatsPromise: Promise<{
    mintedItemsCount: number
    itemsInCommon: Item.ProfilePreview[]
  }>
}

//#endregion
